"use client";
import React, { Suspense, useEffect, useState } from "react";
import {
  Typography,
  Card,
  CardBody,
  Avatar,
  Button,
} from "@material-tailwind/react";
import Image from "next/image";
import recommenderService, {
  useRecommenderState,
} from "@/features/Recommender/services";
import CopyButton from "@/components/CopyButton";
import { useSearchParams } from "next/navigation";
import { useIsLoggedIn } from "@/features/auth/services";
import profileService from "@/features/profile/service";

interface Props {
  id?: number;
  hasButton?: boolean;
  buttonType?: "GO_TO_REGISTER" | "DOWNLOAD_FORM";
}

const RecommenderCard = ({
  id,
  hasButton = true,
  buttonType = "DOWNLOAD_FORM",
}: Props) => {
  return (
    <Suspense fallback={null}>
      <CardQueryParam id={id} hasButton={hasButton} buttonType={buttonType} />
    </Suspense>
  );
};

export default RecommenderCard;

const CardQueryParam = ({
  id,
  hasButton = true,
  buttonType = "DOWNLOAD_FORM",
}: Props) => {
  const searchParams = useSearchParams();
  const recommender = useRecommenderState.getState();
  const [isLoading, setIsLoading] = useState(false);
  const isLoggedIn = useIsLoggedIn();

  useEffect(() => {
    const getRecommender = async () => {
      try {
        setIsLoading(true);
        if (id) {
          await recommenderService.getMemberById(id);
        } else {
          const params = new URLSearchParams(searchParams.toString());
          let id = Number.isNaN(Number(params.get("u")))
            ? recommenderService.getId()
            : Number(params.get("u"));
          if (isLoggedIn) {
            const user = await profileService.getUser();
            if (user) {
              id = user.id;
            }
          } else {
            id = id ?? 1;
            id = id === 0 ? recommenderService.getId() : id;
          }
          await recommenderService.getMemberById(id);
        }
      } catch (error) {
      } finally {
        setIsLoading(false);
      }
    };
    getRecommender();
  }, [id]);

  if (isLoading || !recommender) return <Placeholder />;

  return (
    <div className="container max-w-screen-lg mx-auto">
      <Card color="transparent" shadow={false} className="py-8 lg:flex-row">
        <CardBody className="w-full lg:gap-10 h-full lg:!flex justify-between ">
          <div className="w-full mb-10 lg:mb-0">
            <Typography
              variant="h3"
              color="blue-gray"
              className="text-xl mb-4 font-bold lg:max-w-xs font-noto"
            >
              ที่ปรึกษาธุรกิจ
            </Typography>
            <Typography
              variant="paragraph"
              color="blue-gray"
              className="mb-3 text-lg font-noto"
            >
              ผู้แนะนำ - {recommender.firstName} {recommender.lastName}
            </Typography>
            <div className="flex flex-row gap-1 items-center mb-3">
              <Image
                src="/icons/phone.svg"
                alt=""
                width={24}
                height={24}
                className="flex-shrink-0"
              />
              <Typography
                variant="paragraph"
                color="blue-gray"
                className="mb-0 text-lg font-noto flex items-center"
              >
                {recommender.phoneNumber}
              </Typography>
            </div>
            <div className="flex flex-row gap-1 items-center mb-3">
              <Image
                src="/icons/mail.svg"
                alt=""
                width={24}
                height={24}
                className="flex-shrink-0"
              />
              <Typography
                variant="paragraph"
                color="blue-gray"
                className="mb-0 text-lg font-noto flex items-center"
              >
                {recommender.email}
              </Typography>
            </div>
            <Typography
              variant="paragraph"
              color="blue-gray"
              className="mb-3 text-lg font-noto"
            >
              รหัส {recommender.srikrungMemberId}
            </Typography>
            <Typography
              variant="paragraph"
              color="blue-gray"
              className="mb-3 text-lg font-noto"
            >
              ตำแหน่งระดับ {recommender.membershipPosition}
            </Typography>

            <div className="flex items-center gap-4 mt-3">
              <a href={recommender.otherUrl} target="_blank">
                <Avatar
                  variant="rounded"
                  src="/icons/user.png"
                  alt="facebook"
                  size="sm"
                  className="cursor-pointer"
                />
              </a>
              <div className="w-[1px] h-[36px] bg-blue-gray-100 "></div>
              <a href={recommender.facebook} target="_blank">
                <Avatar
                  variant="rounded"
                  src="/icons/facebook.svg"
                  alt="facebook"
                  size="sm"
                  className="cursor-pointer"
                />
              </a>
              <div className="w-[1px] h-[36px] bg-blue-gray-100 "></div>
              <a href={recommender.line} target="_blank">
                <Avatar
                  variant="rounded"
                  src="/icons/line.png"
                  alt="line"
                  size="sm"
                  className="cursor-pointer"
                />
              </a>
              <div className="w-[1px] h-[36px] bg-blue-gray-100" />
              <a href={recommender.youtube} target="_blank">
                <Avatar
                  variant="rounded"
                  src="/icons/youtube.svg"
                  alt="youtube"
                  // size="sm"
                  className="cursor-pointer"
                />
              </a>
              <div className="w-[1px] h-[36px] bg-blue-gray-100" />
              <CopyButton />
            </div>
            {hasButton && (
              <>
                {buttonType === "DOWNLOAD_FORM" ? (
                  <a
                    href={recommender.registerFormUrl}
                    target="_blank"
                    data-name="download-form"
                  >
                    <Button
                      className="mt-8 w-[200px] bg-[#188F46] font-noto text-lg"
                      name="download-form"
                    >
                      ดาวน์โหลดใบสมัคร
                    </Button>
                  </a>
                ) : (
                  <a href="/member-register" data-name="register-online">
                    <Button
                      className="mt-8 w-[200px] bg-[#188F46] font-noto text-lg"
                      name="register-online"
                    >
                      สมัครสมาชิกออนไลน์
                    </Button>
                  </a>
                )}
              </>
            )}
          </div>
          {recommender.avatarUrl && (
            <div className="h-[21rem] rounded-lg w-full sm:w-[18rem] shrink-0">
              <Image
                width={600}
                height={800}
                alt="testimonial image"
                src={recommender.avatarUrl}
                className="h-full rounded-lg w-full object-cover"
              />
            </div>
          )}
        </CardBody>
      </Card>
    </div>
  );
};

const Placeholder = () => {
  return (
    <div className="container max-w-screen-lg mx-auto">
      <Card color="transparent" shadow={false} className="py-8 lg:flex-row">
        <CardBody className="w-full lg:gap-10 h-full lg:!flex justify-between">
          <div className="w-full mb-10 lg:mb-0">
            <div className="bg-gray-200 rounded h-6 w-40 mb-4" />
            <div className="bg-gray-200 rounded h-5 w-60 mb-3" />

            <div className="flex flex-row gap-1 items-center mb-3">
              <div className="bg-gray-300 rounded w-6 h-6" />
              <div className="bg-gray-200 rounded h-5 w-32" />
            </div>
            <div className="flex flex-row gap-1 items-center mb-3">
              <div className="bg-gray-300 rounded w-6 h-6" />
              <div className="bg-gray-200 rounded h-5 w-48" />
            </div>

            <div className="bg-gray-200 rounded h-5 w-40 mb-3" />
            <div className="bg-gray-200 rounded h-5 w-44 mb-3" />

            <div className="flex items-center gap-4 mt-3">
              <div className="bg-gray-300 rounded w-10 h-10" />
              <div className="w-[1px] h-[36px] bg-blue-gray-100" />
              <div className="bg-gray-300 rounded w-10 h-10" />
              <div className="w-[1px] h-[36px] bg-blue-gray-100" />
              <div className="bg-gray-300 rounded w-10 h-10" />
            </div>

            <div className="mt-8">
              <div className="bg-gray-300 rounded h-10 w-[200px]" />
            </div>
          </div>

          <div className="h-[21rem] rounded-lg w-full sm:w-[18rem] shrink-0 bg-gray-300 animate-pulse" />
        </CardBody>
      </Card>
    </div>
  );
};
